import { useTranslation } from "react-i18next"
import { useDefaultInfiniteQuery } from "@/utils/useDefaultInfiniteQuery"
import { useDebouncedValue } from "@mantine/hooks"
import {
  GetPaginatedSimpleUsersWithPersonNameParams,
  SimpleUserPagination,
} from "@/orval/gen/model"
import {
  InfiniteData,
  UseInfiniteQueryResult,
  UseSuspenseInfiniteQueryResult,
} from "@tanstack/react-query"
import {
  getPaginatedSimpleUsersWithPersonName,
  getPaginatedSimpleUsersWithPersonNameForAdmin,
} from "@/orval/gen"
import { fetchSimpleUserById } from "@/-dataloaders/simple-users"
import {
  SingleSelectFromInfiniteQuery,
  SingleSelectFromInfiniteQueryProps,
} from "./SelectFromQuery/SingleSelectFromInfiniteQuery"
import {
  SelectFromInfiniteQuery,
  SelectFromInfiniteQueryProps,
} from "./SelectFromQuery/SelectFromInfiniteQuery"
import { useDefaultSuspenseInfiniteQuery } from "@/utils/useDefaultSuspenseInfiniteQuery"

interface Props
  extends Omit<
    SingleSelectFromInfiniteQueryProps<
      UseInfiniteQueryResult<InfiniteData<SimpleUserPagination, unknown>, Error>
    >,
    "queryData" | "entitiesToComboboxData" | "searchValue" | "onSearchChange"
  > {
  filterOptions: Omit<
    GetPaginatedSimpleUsersWithPersonNameParams,
    "pageRequest"
  >
}
export function InfiniteUserSelect({ filterOptions, ...props }: Props) {
  const [search, setSearch] = React.useState("")
  const [debouncedSearch] = useDebouncedValue(search, 200)
  const queryResult = useDefaultInfiniteQuery(
    getPaginatedSimpleUsersWithPersonName,
    (pageRequest) => ({
      ...pageRequest,
      searchString: debouncedSearch,
      ...filterOptions,
    }),
    { pageSize: 25 }
  )

  const { t } = useTranslation("app_server")
  return (
    <SingleSelectFromInfiniteQuery
      queryData={queryResult}
      entitiesToComboboxData={(u) => ({
        label: u.userName,
        value: u.userId.toString(),
      })}
      fetchSelectedItem={fetchSimpleUserById}
      getSelectedItemQueryKey={(id) => ["SimpleUser", parseInt(id, 10)]}
      searchable
      placeholder={t("Search") + "..."}
      searchValue={search}
      onSearchChange={(v) => setSearch(v)}
      {...props}
    />
  )
}

interface MultiSelectProps
  extends Omit<
    SelectFromInfiniteQueryProps<
      UseSuspenseInfiniteQueryResult<
        InfiniteData<SimpleUserPagination, unknown>,
        Error
      >
    >,
    | "queryData"
    | "entitiesToComboboxData"
    | "searchValue"
    | "onSearchChange"
    | "onChange"
  > {
  filterOptions: Omit<
    GetPaginatedSimpleUsersWithPersonNameParams,
    "pageRequest"
  >
}

export function InfiniteUserMultiSelect({
  filterOptions,
  ...props
}: MultiSelectProps) {
  const [search, setSearch] = React.useState("")
  const [debouncedSearch] = useDebouncedValue(search, 200)
  const queryResult = useDefaultSuspenseInfiniteQuery(
    getPaginatedSimpleUsersWithPersonName,
    (pageRequest) => ({
      ...pageRequest,
      searchString: debouncedSearch,
      ...filterOptions,
    }),
    { pageSize: 25 }
  )

  const { t } = useTranslation("app_server")
  return (
    <SelectFromInfiniteQuery
      multiple
      queryData={queryResult}
      entitiesToComboboxData={(u) => ({
        label: u.userName,
        value: u.userId.toString(),
      })}
      fetchSelectedItem={fetchSimpleUserById}
      getSelectedItemQueryKey={(id) => ["SimpleUser", parseInt(id, 10)]}
      searchable
      placeholder={t("Search") + "..."}
      searchValue={search}
      onSearchChange={(v) => setSearch(v)}
      {...props}
    />
  )
}

interface AdminProps
  extends Omit<
    SingleSelectFromInfiniteQueryProps<
      UseInfiniteQueryResult<InfiniteData<SimpleUserPagination, unknown>, Error>
    >,
    "queryData" | "entitiesToComboboxData" | "searchValue" | "onSearchChange"
  > {
  filterOptions: Omit<
    GetPaginatedSimpleUsersWithPersonNameParams,
    "pageRequest"
  >
}
export function AdminInfiniteUserSelect({
  filterOptions,
  ...props
}: AdminProps) {
  const [search, setSearch] = React.useState("")
  const [debouncedSearch] = useDebouncedValue(search, 200)
  const queryResult = useDefaultInfiniteQuery(
    getPaginatedSimpleUsersWithPersonNameForAdmin,
    (pageRequest) => ({
      ...pageRequest,
      searchString: debouncedSearch,
      ...filterOptions,
    }),
    { pageSize: 25 }
  )

  const { t } = useTranslation("app_server")
  return (
    <SingleSelectFromInfiniteQuery
      queryData={queryResult}
      entitiesToComboboxData={(u) => ({
        label: u.userName,
        value: u.userId.toString(),
      })}
      fetchSelectedItem={fetchSimpleUserById}
      getSelectedItemQueryKey={(id) => ["SimpleUser", parseInt(id, 10)]}
      searchable
      placeholder={t("Search") + "..."}
      searchValue={search}
      onSearchChange={(v) => setSearch(v)}
      {...props}
    />
  )
}
